import React from "react";
import { UseFormRegister, FieldErrors, Controller, Control, useWatch } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { CampaignTypeSelector } from "../../components/campaign-type-selector";
import { LinkedCampaignCombobox } from "../../components/linked-campaign-combobox";
import { type UpdateCampaignRouletteFormData } from "../../validations/campaign-roulette.validation";
import { CampaignType } from "@/@types/campaign-roulette";
import { LinkedCampaignOption } from "../../hooks/useLinkedCampaignSearch";

interface BasicInfoCardProps {
  register: UseFormRegister<UpdateCampaignRouletteFormData>;
  control: Control<UpdateCampaignRouletteFormData>;
  errors: FieldErrors<UpdateCampaignRouletteFormData>;
  campaignNameLength: number;
  linkedCampaignSearch: {
    query: string;
    onQueryChange: (q: string) => void;
    results: LinkedCampaignOption[];
    isLoading: boolean;
  };
  selectedLinkedCampaignName: string | null;
  onLinkedCampaignSelect: (id: number | null, name: string | null) => void;
  linkedCampaignDisabled?: boolean;
}

export const BasicInfoCard: React.FC<BasicInfoCardProps> = ({
  register,
  control,
  errors,
  campaignNameLength,
  linkedCampaignSearch,
  selectedLinkedCampaignName,
  onLinkedCampaignSelect,
  linkedCampaignDisabled = false,
}) => {
  const campaignType = useWatch({ control, name: "campaignType" });
  const linkedCampaignId = useWatch({ control, name: "linkedCampaignId" });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Informações Básicas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Controller
          control={control}
          name="campaignType"
          render={({ field }) => (
            <CampaignTypeSelector
              value={field.value ?? CampaignType.ROULETTE}
              onChange={field.onChange}
              disabled
            />
          )}
        />
        {errors.campaignType && (
          <p className="text-sm text-red-500 -mt-3">
            {errors.campaignType.message}
          </p>
        )}

        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor="campaignName">Nome da Campanha *</Label>
            <span className="text-xs text-gray-500">
              {campaignNameLength}/33
            </span>
          </div>
          <Input
            id="campaignName"
            placeholder="Ex: Black Friday 2024"
            maxLength={33}
            {...register("campaignName")}
          />
          {errors.campaignName && (
            <p className="text-sm text-red-500 mt-1">
              {errors.campaignName.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="startDate">Data de Início *</Label>
            <Input id="startDate" type="date" {...register("startDate")} />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.startDate.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="endDate">Data de Fim *</Label>
            <Input id="endDate" type="date" {...register("endDate")} />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.endDate.message}
              </p>
            )}
          </div>
        </div>

        {campaignType === CampaignType.WORLD_CUP && (
          <LinkedCampaignCombobox
            query={linkedCampaignSearch.query}
            onQueryChange={linkedCampaignSearch.onQueryChange}
            results={linkedCampaignSearch.results}
            isLoading={linkedCampaignSearch.isLoading}
            selectedId={linkedCampaignId ?? null}
            selectedName={selectedLinkedCampaignName}
            onSelect={onLinkedCampaignSelect}
            error={errors.linkedCampaignId?.message}
            disabled={linkedCampaignDisabled}
          />
        )}
      </CardContent>
    </Card>
  );
};
